import { useState, useEffect, useMemo } from 'react';
import type { ResourceBudget } from '@/types/budget.types';
import { BudgetCalculationService, fetchLiveRoleRates } from '@/services/budgetCalculation.service';
import type { BudgetCalculationResult, LiveRoleRateMap } from '@/services/budgetCalculation.service';
import { getDataMode } from '@/lib/data-service';

export const useBudgetCalculations = (projectId: string, resourceBudget: ResourceBudget | null) => {
  const [calculation, setCalculation] = useState<BudgetCalculationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rateVersion, setRateVersion] = useState(0);
  // In live mode the role rates come from the live Rate Card (effectiveBillRate),
  // fetched asynchronously. In demo/tour mode this stays null and the calculation
  // service falls back to the mock-backed RateCardStorageService.
  const [liveRoleRates, setLiveRoleRates] = useState<LiveRoleRateMap | null>(null);

  // Listen for rate changes in localStorage
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Check if rate-related keys changed
      if (e.key && (e.key.includes('projectRates') || e.key.includes('personRates'))) {
        setRateVersion(prev => prev + 1);
      }
    };

    // Also listen for custom events (for same-tab changes)
    const handleRateChange = () => {
      setRateVersion(prev => prev + 1);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('rateCardUpdated', handleRateChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('rateCardUpdated', handleRateChange);
    };
  }, [projectId]);

  // Load live Rate Card role rates in live mode so budget calculations use the
  // same effectiveBillRate as the Rate Card view and the backend Labour cascade.
  useEffect(() => {
    let cancelled = false;
    if (getDataMode() === 'live' && projectId) {
      fetchLiveRoleRates(projectId).then(rates => {
        if (!cancelled) setLiveRoleRates(rates);
      });
    } else {
      setLiveRoleRates(null);
    }
    return () => {
      cancelled = true;
    };
  }, [projectId, rateVersion]);

  // Memoize the calculation to avoid unnecessary recalculations.
  // rateVersion is an intentional dependency: it forces recalculation when
  // rate cards change in localStorage (read internally by calculateBudget).
  const calculatedBudget = useMemo(() => {
    if (!projectId || !resourceBudget) {
      return null;
    }

    try {
      const result = BudgetCalculationService.calculateBudget(
        projectId,
        resourceBudget,
        liveRoleRates ?? undefined
      );
      return result;
    } catch (err) {
      console.error('[useBudgetCalculations] Calculation error:', err);
      setError(err instanceof Error ? err.message : 'Calculation failed');
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, resourceBudget, rateVersion, liveRoleRates]);

  useEffect(() => {
    setCalculation(calculatedBudget);
  }, [calculatedBudget]);

  /**
   * Calculate the impact of a rate change
   */
  const calculateRateChangeImpact = (
    roleId: string,
    newCostRate: number,
    newBillRate: number,
    effectiveFrom: string
  ) => {
    if (!resourceBudget) {
      return {
        revenueImpact: 0,
        costImpact: 0,
        marginImpact: 0,
        affectedMonths: [],
        remainingHours: 0,
      };
    }

    return BudgetCalculationService.calculateRateChangeImpact(
      projectId,
      resourceBudget,
      roleId,
      newCostRate,
      newBillRate,
      effectiveFrom
    );
  };

  /**
   * Recalculate budget (useful after rate changes)
   */
  const recalculate = () => {
    if (!projectId || !resourceBudget) return;

    setLoading(true);
    try {
      const result = BudgetCalculationService.calculateBudget(
        projectId,
        resourceBudget,
        liveRoleRates ?? undefined
      );
      setCalculation(result);
      setError(null);
    } catch (err) {
      console.error('[useBudgetCalculations] Recalculation error:', err);
      setError(err instanceof Error ? err.message : 'Recalculation failed');
    } finally {
      setLoading(false);
    }
  };

  return {
    calculation,
    loading,
    error,
    calculateRateChangeImpact,
    recalculate,
    
    // Convenience accessors
    totalLabourCost: calculation?.totalLabourCost || 0,
    totalRevenue: calculation?.totalRevenue || 0,
    totalMargin: calculation?.totalMargin || 0,
    marginPercentage: calculation?.marginPercentage || 0,
    roleBreakdown: calculation?.roleBreakdown || [],
  };
};
